import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as FileIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';
import {
  uploadBulkFile,
  getBulkUploadJobs,
  getBulkUploadJob,
  downloadBulkUploadTemplate,
  downloadJobErrors,
  type BulkUploadJob
} from '../services/apiService';

interface UploadError {
  message: string;
  details?: string[];
  hint?: string;
}

const BulkUpload: React.FC = () => {
  const [jobs, setJobs] = useState<BulkUploadJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<UploadError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [expandedJobDetails, setExpandedJobDetails] = useState<BulkUploadJob | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Polling ref to track active polling intervals
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBulkUploadJobs(pagination.page, pagination.limit);
      setJobs(response.jobs || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, pages: 1 });
    } catch (err: any) {
      setError({ message: err.message || 'Failed to fetch jobs' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll for processing jobs
  useEffect(() => {
    const hasProcessingJobs = jobs.some(job => job.status === 'pending' || job.status === 'processing');

    if (hasProcessingJobs && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchJobs();
      }, 3000);
    } else if (!hasProcessingJobs && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobs, fetchJobs]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      validateAndSetFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];

    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError({ message: 'Please upload an Excel file (.xlsx or .xls)' });
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError({ message: 'Please select a file to upload' });
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await uploadBulkFile(selectedFile);

      if (response.success) {
        setSuccess(`File "${selectedFile.name}" uploaded successfully. Processing started.`);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchJobs();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        // Handle detailed error response
        const errorResponse = response as any;
        setError({
          message: errorResponse.error || 'Upload failed',
          details: errorResponse.details,
          hint: errorResponse.hint
        });
      }
    } catch (err: any) {
      // Handle error thrown from API service
      const errorData = err.details || err;
      setError({
        message: errorData.error || err.message || 'Upload failed',
        details: errorData.details,
        hint: errorData.hint
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadBulkUploadTemplate();
    } catch (err: any) {
      setError({ message: err.message || 'Failed to download template' });
    }
  };

  const handleDownloadErrors = async (jobId: string) => {
    try {
      await downloadJobErrors(jobId);
    } catch (err: any) {
      setError({ message: err.message || 'Failed to download errors' });
    }
  };

  const handleExpandJob = async (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setExpandedJobDetails(null);
    } else {
      try {
        const job = await getBulkUploadJob(jobId);
        setExpandedJobId(jobId);
        setExpandedJobDetails(job);
      } catch (err: any) {
        setError({ message: err.message || 'Failed to fetch job details' });
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon style={{ fontSize: '0.875rem' }} />
            Completed
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ErrorIcon style={{ fontSize: '0.875rem' }} />
            Partial
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon style={{ fontSize: '0.875rem' }} />
            Failed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <RefreshIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
            Processing
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <PendingIcon style={{ fontSize: '0.875rem' }} />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  return (
    <MainLayout title="Bulk Upload">
      <div className="p-2">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Upload</h1>
          <p className="mt-1 text-gray-600">
            Upload Excel files to bulk import objections and FAQs. The system will automatically generate variations and categorize each item.
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircleIcon className="text-green-600 mr-2" style={{ fontSize: '1.25rem' }} />
            <span className="text-green-800">{success}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <XCircleIcon className="text-red-600 mr-2 mt-0.5 flex-shrink-0" style={{ fontSize: '1.25rem' }} />
              <div className="flex-1">
                <p className="text-red-800 font-medium">{error.message}</p>
                {error.details && error.details.length > 0 && (
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                    {error.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                )}
                {error.hint && (
                  <p className="mt-2 text-sm text-red-600 italic">{error.hint}</p>
                )}
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0"
              >
                <XCircleIcon style={{ fontSize: '1rem' }} />
              </button>
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Upload Excel File</h2>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <DownloadIcon style={{ fontSize: '1.25rem' }} />
              Download Template
            </button>
          </div>

          {/* Info Box */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <InfoIcon className="text-blue-600 mt-0.5" style={{ fontSize: '1.25rem' }} />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Excel File Format:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>objections</strong> sheet: Columns "Objection" and "Response"</li>
                  <li><strong>faqs</strong> sheet: Columns "Question" and "Answer"</li>
                </ul>
                <p className="mt-2">Each row will be processed to generate 5-6 variations and auto-categorized.</p>
              </div>
            </div>
          </div>

          {/* Drag & Drop Zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : selectedFile
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            {selectedFile ? (
              <div className="flex flex-col items-center">
                <FileIcon className="text-green-600 mb-2" style={{ fontSize: '3rem' }} />
                <p className="text-lg font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-700"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <UploadIcon className="text-gray-400 mb-2" style={{ fontSize: '3rem' }} />
                <p className="text-lg font-medium text-gray-900">
                  Drag & drop your Excel file here
                </p>
                <p className="text-sm text-gray-500 mt-1">or click to browse</p>
                <p className="text-xs text-gray-400 mt-2">Supported formats: .xlsx, .xls</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${
                !selectedFile || uploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {uploading ? (
                <>
                  <RefreshIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadIcon style={{ fontSize: '1.25rem' }} />
                  Upload & Process
                </>
              )}
            </button>
          </div>
        </div>

        {/* Job History Section */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Upload History</h2>
            <button
              onClick={fetchJobs}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1rem' }} />
              Refresh
            </button>
          </div>

          {loading && jobs.length === 0 ? (
            <div className="p-8 text-center">
              <RefreshIcon className="animate-spin mx-auto text-gray-400" style={{ fontSize: '2rem' }} />
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No upload history found. Upload a file to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Date/Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">File Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Objections</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">FAQs</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {jobs.map((job) => (
                    <React.Fragment key={job._id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(job.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <FileIcon className="text-gray-400" style={{ fontSize: '1.25rem' }} />
                            {job.fileName}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(job.status)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <span className="text-green-600 font-medium">
                            {job.processedObjections - job.failedObjections}
                          </span>
                          {job.failedObjections > 0 && (
                            <span className="text-red-600"> / -{job.failedObjections}</span>
                          )}
                          <span className="text-gray-400"> of {job.totalObjections}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <span className="text-green-600 font-medium">
                            {job.processedFaqs - job.failedFaqs}
                          </span>
                          {job.failedFaqs > 0 && (
                            <span className="text-red-600"> / -{job.failedFaqs}</span>
                          )}
                          <span className="text-gray-400"> of {job.totalFaqs}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleExpandJob(job._id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            {expandedJobId === job._id ? (
                              <>
                                <ExpandLessIcon style={{ fontSize: '1rem' }} />
                                Hide Details
                              </>
                            ) : (
                              <>
                                <ExpandMoreIcon style={{ fontSize: '1rem' }} />
                                View Details
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded Details Row */}
                      {expandedJobId === job._id && expandedJobDetails && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="space-y-4">
                              {/* Summary Stats */}
                              <div className="grid grid-cols-4 gap-4">
                                <div className="bg-white p-3 rounded-lg border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Total Items</p>
                                  <p className="text-xl font-semibold text-gray-900">
                                    {expandedJobDetails.totalObjections + expandedJobDetails.totalFaqs}
                                  </p>
                                </div>
                                <div className="bg-white p-3 rounded-lg border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Processed</p>
                                  <p className="text-xl font-semibold text-green-600">
                                    {expandedJobDetails.processedObjections + expandedJobDetails.processedFaqs}
                                  </p>
                                </div>
                                <div className="bg-white p-3 rounded-lg border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Successful</p>
                                  <p className="text-xl font-semibold text-green-600">
                                    {(expandedJobDetails.processedObjections - expandedJobDetails.failedObjections) +
                                     (expandedJobDetails.processedFaqs - expandedJobDetails.failedFaqs)}
                                  </p>
                                </div>
                                <div className="bg-white p-3 rounded-lg border border-gray-200">
                                  <p className="text-xs text-gray-500 uppercase">Failed</p>
                                  <p className="text-xl font-semibold text-red-600">
                                    {expandedJobDetails.failedObjections + expandedJobDetails.failedFaqs}
                                  </p>
                                </div>
                              </div>

                              {/* Error List */}
                              {expandedJobDetails.errors && expandedJobDetails.errors.length > 0 && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-gray-900">
                                      Errors ({expandedJobDetails.errors.length})
                                    </h4>
                                    <button
                                      onClick={() => handleDownloadErrors(job._id)}
                                      className="inline-flex items-center gap-1 px-3 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                    >
                                      <DownloadIcon style={{ fontSize: '0.875rem' }} />
                                      Download CSV
                                    </button>
                                  </div>
                                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Row</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Sheet</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Error</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Original Data</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {expandedJobDetails.errors.slice(0, 10).map((error, idx) => (
                                          <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-gray-900">{error.row}</td>
                                            <td className="px-3 py-2 text-gray-900">{error.sheet}</td>
                                            <td className="px-3 py-2 text-red-600">{error.message}</td>
                                            <td className="px-3 py-2 text-gray-500 text-xs max-w-xs truncate">
                                              {error.originalData?.question || '-'}
                                            </td>
                                          </tr>
                                        ))}
                                        {expandedJobDetails.errors.length > 10 && (
                                          <tr>
                                            <td colSpan={4} className="px-3 py-2 text-center text-gray-500 text-xs">
                                              ... and {expandedJobDetails.errors.length - 10} more errors. Download CSV for full list.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* No errors message */}
                              {(!expandedJobDetails.errors || expandedJobDetails.errors.length === 0) && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                  <CheckCircleIcon className="text-green-600 mb-2" style={{ fontSize: '2rem' }} />
                                  <p className="text-green-800">All items were processed successfully!</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page === pagination.pages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default BulkUpload;
